import { useNavigate, useLocation } from "react-router-dom"
import { LogOut, Search, ChevronLeft } from "lucide-react"
import { useAuthStore } from "@/store/authStore"
import { useUiStore } from "@/store/uiStore"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import { getInitials } from "@/lib/utils"
import { convencion, estiloBandera } from "@/lib/pais"
import { navFor, tituloDe, rutaPadre } from "../nav/navConfig"
import ThemePicker from "../ThemePicker"

/**
 * Encabezado. Teléfono: flecha de volver en las pantallas de detalle, título, lupa y avatar (el menú
 * vive en la barra inferior; ya no hay hamburguesa con un segundo cajón). Escritorio: buscador
 * (Ctrl K), país del asesor y avatar con la cuenta y el tema.
 */
export default function Header() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, partner, logout } = useAuthStore()
  const abrirBuscador = useUiStore((s) => s.abrirBuscador)

  const def = navFor(partner)
  const padre = rutaPadre(location.pathname)
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ")
  const initials = getInitials(fullName || user?.email)
  const esCd2b = partner?.organizationType === "cd2b_consultant"

  const handleLogout = async () => {
    await logout()
    navigate("/login")
  }

  return (
    <header className="sticky top-0 z-40 border-b border-line bg-background/90 backdrop-blur-md pt-safe">
      <div className="flex h-14 items-center gap-1 px-2 md:h-16 md:gap-3 md:px-6">
        {padre ? (
          <button
            type="button"
            onClick={() => navigate(padre)}
            className="md:hidden inline-flex h-11 w-11 items-center justify-center rounded-md text-neutral-700 dark:text-neutral-300 hover:bg-surface-muted"
            aria-label="Volver"
          >
            <ChevronLeft size={22} />
          </button>
        ) : (
          <span className="md:hidden w-2" />
        )}

        <h1 className="md:hidden flex-1 min-w-0 font-display text-h3 font-semibold text-foreground truncate">
          {tituloDe(location.pathname, def)}
        </h1>

        {/* Escritorio: buscador */}
        <button
          type="button"
          onClick={abrirBuscador}
          className="hidden md:flex items-center gap-2 h-10 w-full max-w-sm rounded-md border border-line bg-card px-3 text-body-sm text-subtle hover:border-neutral-300 dark:hover:border-neutral-600 transition-colors"
        >
          <Search size={16} aria-hidden="true" />
          <span className="flex-1 text-left">{esCd2b ? "Buscar empresa, cotización…" : "Buscar…"}</span>
          <kbd className="rounded border border-line bg-surface-muted px-1.5 text-micro font-sans text-subtle">Ctrl K</kbd>
        </button>
        <div className="hidden md:block flex-1" />

        <button
          type="button"
          onClick={abrirBuscador}
          className="md:hidden inline-flex h-11 w-11 items-center justify-center rounded-md text-neutral-700 dark:text-neutral-300 hover:bg-surface-muted"
          aria-label="Buscar"
        >
          <Search size={20} />
        </button>

        {esCd2b && partner?.country && (
          <span className="hidden lg:inline-flex items-center gap-2 rounded-full border border-line bg-card px-3 h-9 text-body-sm font-medium text-foreground">
            <span aria-hidden="true" className="h-3 w-[18px] rounded-[2px] ring-1 ring-line" style={estiloBandera(partner.country)} />
            {convencion(partner.country).nombre || partner.country}
            {partner.rolCd2b === "gerente" && <span className="text-caption text-subtle">· Gerente</span>}
          </span>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-full p-1 hover:bg-surface-muted transition-colors"
              aria-label="Menú de usuario"
            >
              <Avatar className="h-9 w-9">
                <AvatarImage src={partner?.logoUrl || undefined} alt={fullName} />
                <AvatarFallback className="text-body-sm">{initials}</AvatarFallback>
              </Avatar>
              <span className="hidden xl:flex flex-col items-start pr-2">
                <span className="text-body-sm font-semibold text-foreground leading-tight">{fullName || user?.email}</span>
                <span className="text-caption text-subtle leading-tight">{partner?.organizationName || "Partner"}</span>
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel>
              <div className="font-semibold text-foreground normal-case tracking-normal text-body-sm">{fullName || user?.email}</div>
              <div className="text-caption text-subtle font-normal normal-case tracking-normal truncate">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {def.cuenta.map((it) => (
              <DropdownMenuItem key={it.path} onClick={() => navigate(it.path)}>
                {it.icon} {it.text}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
              <p className="mb-1.5 text-micro uppercase tracking-wider text-subtle">Tema</p>
              <ThemePicker />
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-danger-700 dark:text-danger-300">
              <LogOut size={16} /> Cerrar sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
